import { useEffect } from "react";
import type { CSSProperties } from "react";
import { useParams } from "react-router-dom";
import { useLiveStore } from "../state/liveStore";
import LiveShareToggle from "../components/LiveShareToggle";

interface DriverState {
  userId: string;
  displayName: string;
  position: number;
  lapCompleted: number;
  fuelLevelL: number;
  fuelPct: number;
  lapsRemainingAvg: number | null;
  lapsRemainingWorst: number | null;
  fuelPerLapAvgL: number | null;
  onPitRoad: boolean;
  isGreen: boolean;
  isCaution: boolean;
  sessionLapsRemainEx: number;
  lastUpdate: number;
}

const L_TO_GAL = 0.264172;

export default function CrewChief() {
  const { teamId } = useParams<{ teamId: string }>();
  // Consume the SHARED, consent-aware live store (not a bespoke socket): the
  // crew-chief view gets the consent_revoked drop + the shared stale-out for free,
  // and we drop a duplicate WebSocket that bypassed the consent layer (#291).
  const { drivers: liveDrivers, connected, connect, disconnect } = useLiveStore();

  useEffect(() => {
    if (!teamId) return;
    connect(teamId);
    return () => disconnect();
  }, [teamId, connect, disconnect]);

  const sorted: DriverState[] = Object.values(liveDrivers)
    .map((d) => ({
      userId: d.userId,
      displayName: d.displayName,
      position: d.frame.position ?? 0,
      lapCompleted: d.frame.lapCompleted ?? 0,
      fuelLevelL: d.frame.fuelLevelL ?? 0,
      fuelPct: d.frame.fuelPct ?? 0,
      lapsRemainingAvg: d.frame.lapsRemainingAvg ?? null,
      lapsRemainingWorst: d.frame.lapsRemainingWorst ?? null,
      fuelPerLapAvgL: d.frame.fuelPerLapAvgL ?? null,
      onPitRoad: d.frame.onPitRoad ?? false,
      isGreen: d.frame.isGreen ?? false,
      isCaution: d.frame.isCaution ?? false,
      sessionLapsRemainEx: d.frame.sessionLapsRemainEx ?? 0,
      lastUpdate: d.lastSeen,
    }))
    .sort((a, b) => (a.position || 99) - (b.position || 99));

  return (
    <div className="page page--wide fade-in">
      <header className="editorial-header">
        <div className="editorial-meta">
          <div className="editorial-meta-left">
            <span className="eyebrow">Live race control</span>
          </div>
          <div className="editorial-meta-right" style={st.metaRight}>
            <span className={`badge ${connected ? "badge-green" : "badge-red"}`} style={st.liveBadge}>
              <span style={{
                width: 6, height: 6, borderRadius: "50%",
                background: "currentColor",
                animation: connected ? "pulse 2s infinite" : undefined,
              }} />
              {connected ? "Live" : "Connecting…"}
            </span>
            {sorted.length > 0 && (
              <span className="chip">{sorted.length} driver{sorted.length !== 1 ? "s" : ""} broadcasting</span>
            )}
          </div>
        </div>
        <h1 className="editorial-title">Crew Chief</h1>
      </header>

      {/* Your own live-share consent (#291). Live sharing is opt-in: teammates only
          see your data here once you turn this on. */}
      {teamId && (
        <div style={{ marginBottom: "1.5rem", maxWidth: 480 }}>
          <LiveShareToggle teamId={teamId} />
        </div>
      )}

      {sorted.length === 0 ? (
        <div className="empty-state" style={{ minHeight: "16rem" }}>
          <div className="empty-state-title">No drivers broadcasting</div>
          <div className="empty-state-text">
            Live sharing is opt-in: each teammate turns on <strong>Share my live data</strong> above before
            their run shows here. With it on, start the desktop agent with live telemetry and team relay enabled.
          </div>
        </div>
      ) : (
        <>
          {/* Driver table */}
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <div className="xpe-table-scroll">
              <table className="table striped">
                <thead>
                  <tr>
                    <th>Driver</th>
                    <th>Pos</th>
                    <th>Lap</th>
                    <th>Fuel</th>
                    <th>Laps left</th>
                    <th>Burn/lap</th>
                    <th style={{ textAlign: "right" }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((d) => (
                    <DriverRow key={d.userId} driver={d} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pit strategy */}
          <PitStrategyPanel drivers={sorted} />
        </>
      )}
    </div>
  );
}

/* ---------- Sub-components ---------- */

function DriverRow({ driver: d }: { driver: DriverState }) {
  const fuelGal = d.fuelLevelL * L_TO_GAL;
  const burnGal = d.fuelPerLapAvgL !== null ? d.fuelPerLapAvgL * L_TO_GAL : null;
  const critical = d.lapsRemainingWorst !== null && d.lapsRemainingWorst <= 3;

  return (
    <tr>
      <td style={{ fontWeight: 600 }}>{d.displayName}</td>
      <td className="mono">P{d.position || "—"}</td>
      <td className="mono">{d.lapCompleted}</td>
      <td className="mono">
        {fuelGal.toFixed(1)}g
        <span style={{ color: "var(--text-muted)", fontSize: "0.8125rem", marginLeft: 4 }}>
          ({(d.fuelPct * 100).toFixed(0)}%)
        </span>
      </td>
      <td className="mono" style={{ color: critical ? "var(--flag-red)" : undefined, fontWeight: critical ? 700 : undefined }}>
        {d.lapsRemainingWorst ?? "—"}
      </td>
      <td className="mono">{burnGal !== null ? `${burnGal.toFixed(2)} gal` : "—"}</td>
      <td style={{ textAlign: "right" }}>
        {d.onPitRoad ? (
          <span className="badge badge-yellow">Pit</span>
        ) : d.isCaution ? (
          <span className="badge badge-yellow">Caution</span>
        ) : d.isGreen ? (
          <span className="badge badge-green">Green</span>
        ) : (
          <span className="badge" style={{ background: "var(--ink-1)", color: "var(--text-muted)" }}>—</span>
        )}
      </td>
    </tr>
  );
}

function PitStrategyPanel({ drivers }: { drivers: DriverState[] }) {
  const windows = drivers
    .filter((d) => d.fuelPerLapAvgL !== null && d.fuelPerLapAvgL > 0)
    .map((d) => {
      const lapsLeft = d.lapsRemainingWorst ?? 0;
      const windowStart = d.lapCompleted + Math.max(1, Math.floor(lapsLeft * 0.6));
      const windowEnd = d.lapCompleted + lapsLeft;
      return { driver: d, windowStart, windowEnd, lapsLeft };
    })
    .sort((a, b) => a.windowStart - b.windowStart);

  if (windows.length === 0) return null;

  const overlap = windows.length >= 2 &&
    windows[0].windowEnd >= windows[1].windowStart;

  return (
    <section style={st.pitSection}>
      <div className="section-head">
        <span className="section-label">Pit windows</span>
      </div>

      {overlap && (
        <div style={st.overlapWarning}>
          Pit windows overlap — consider staggering stops to avoid double-stacking.
        </div>
      )}

      <div style={st.windowList}>
        {windows.map((w) => {
          const urgent = w.lapsLeft <= 3;
          return (
            <div
              key={w.driver.userId}
              className="card"
              style={{
                ...st.windowCard,
                borderLeft: urgent ? "3px solid var(--flag-red)" : "3px solid var(--live)",
                background: urgent ? "var(--flag-red-soft)" : undefined,
              }}
            >
              <div style={st.windowDriver}>{w.driver.displayName}</div>
              <div className="mono" style={{ ...st.windowRange, color: urgent ? "var(--flag-red)" : "var(--text-strong)" }}>
                L{w.windowStart}–L{w.windowEnd}
              </div>
              <span className="chip" style={{ fontSize: "0.8125rem" }}>
                {w.lapsLeft} laps of fuel
              </span>
              {w.driver.onPitRoad && (
                <span className="badge badge-yellow">In Pit</span>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ---------- Styles ---------- */

const st: Record<string, CSSProperties> = {
  metaRight: {
    display: "flex",
    alignItems: "center",
    gap: "0.6rem",
    flexWrap: "wrap",
  },
  liveBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.4rem",
  },

  pitSection: {
    marginTop: "1.5rem",
  },
  overlapWarning: {
    padding: "0.75rem 1rem",
    borderRadius: "var(--r-sm)",
    background: "var(--flag-yellow-soft)",
    border: "1px solid rgba(250, 204, 21, 0.24)",
    color: "var(--flag-yellow)",
    fontSize: "0.85rem",
    marginBottom: "0.85rem",
  },
  windowList: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
  },
  windowCard: {
    padding: "0.75rem 1rem",
    display: "flex",
    alignItems: "center",
    gap: "0.85rem",
    flexWrap: "wrap",
  },
  windowDriver: {
    fontFamily: "var(--font-display)",
    fontWeight: 700,
    fontSize: "0.92rem",
    minWidth: 110,
    color: "var(--text-strong)",
  },
  windowRange: {
    fontSize: "0.88rem",
    fontWeight: 600,
  },
};
